!function() {
  "use strict";
  
  class CartDrawerRecommendations extends HTMLElement {
    constructor() {
      super();
      const cartIdsAttr = this.getAttribute('data-cart-product-ids');
      this.cartProductIds = cartIdsAttr ? cartIdsAttr.split(',').filter(Boolean) : [];
      this.productLimit = parseInt(this.getAttribute('data-product-limit')) || 20;
      this.isLoaded = false;
      this.isLoading = false;
      this.lastCartIds = '';
      this.lastItemCount = 0;
      this.isFloating = false;
      this.addToCartText = window.ShopifyTranslations?.addToCart || 'Add to cart';
      
      console.log('CartDrawerRecommendations constructor:', {
        cartIdsAttr,
        cartProductIds: this.cartProductIds,
        productLimit: this.productLimit
      });
      
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onCartDrawerOpen = this.onCartDrawerOpen.bind(this);
      this.onCartDrawerClose = this.onCartDrawerClose.bind(this);
      
      // Store product data for variant access
      this.storedProductData = {};
    }

    connectedCallback() {
      console.log('CartDrawerRecommendations connectedCallback - initializing...');
      
      this.setupEventListeners();
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.initializeRecommendations();
        });
      } else {
        setTimeout(() => {
          this.initializeRecommendations();
        }, 100);
      }
      
      console.log('CartDrawerRecommendations connectedCallback - initialization complete');
    }
    
    async initializeRecommendations() {
      console.log('CartDrawerRecommendations initializeRecommendations - starting...');
      await this.checkCartStateAndUpdate();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setupEventListeners() {
      // Theme cart events
      document.addEventListener('theme:cart:add', this.onCartUpdate);
      document.addEventListener('theme:cart:refresh', this.onCartUpdate);
      document.addEventListener('theme:cart:change', this.onCartUpdate);
      document.addEventListener('theme:cart:update', this.onCartUpdate);
      document.addEventListener('theme:cart:remove', this.onCartUpdate);
      
      // Cart drawer events
      document.addEventListener('theme:cart-drawer:open', this.onCartDrawerOpen);
      document.addEventListener('theme:cart-drawer:close', this.onCartDrawerClose);
      document.addEventListener('theme:cart-drawer:show', this.onCartDrawerOpen);
      
      // Product add events
      document.addEventListener('theme:product:add', this.onCartUpdate);
      document.addEventListener('theme:product:added', this.onCartUpdate);
      
      // Variant selector events (event delegation at document level)
      document.addEventListener('change', (e) => {
        if (e.target.classList.contains('cart-drawer-recommendation-item__variant-select')) {
          console.log('Variant selector changed via document event delegation:', e.target.value);
          const item = e.target.closest('.cart-drawer-recommendations__item');
          if (item && item.productData) {
            console.log('Found item via document event delegation:', item);
            this.updateVariantSelection(item);
          }
        }
      });
      
      console.log('Cart recommendations: Listening to theme events and variant selectors');
    }

    removeEventListeners() {
      document.removeEventListener('theme:cart:add', this.onCartUpdate);
      document.removeEventListener('theme:cart:refresh', this.onCartUpdate);
      document.removeEventListener('theme:cart:change', this.onCartUpdate);
      document.removeEventListener('theme:cart:update', this.onCartUpdate);
      document.removeEventListener('theme:cart:remove', this.onCartUpdate);
      
      document.removeEventListener('theme:cart-drawer:open', this.onCartDrawerOpen);
      document.removeEventListener('theme:cart-drawer:close', this.onCartDrawerClose);
      document.removeEventListener('theme:cart-drawer:show', this.onCartDrawerOpen);
      
      document.removeEventListener('theme:product:add', this.onCartUpdate);
      document.removeEventListener('theme:product:added', this.onCartUpdate);
      
      console.log('Cart recommendations: Event listeners removed');
    }

    async onCartUpdate(event) {
      console.log('Cart update detected:', event.type, event.detail, 'checking cart state...');
      await this.checkCartStateAndUpdate();
    }
    
    async checkCartStateAndUpdate() {
      try {
        console.log('Checking actual cart state...');
        
        const response = await fetch('/cart.js');
        if (!response.ok) {
          console.error('Failed to fetch cart data for state check');
          return;
        }
        
        const cartData = await response.json();
        const currentCartItems = cartData.items || [];
        const currentCartIds = currentCartItems.map(item => item.product_id.toString());
        const currentItemCount = currentCartIds.length;
        
        console.log('Current cart state:', {
          currentItemCount,
          lastItemCount: this.lastItemCount,
          productIds: currentCartIds,
          previousIds: this.cartProductIds,
          hasItems: currentItemCount > 0,
          cartTotal: cartData.total_price,
          totalQuantity: cartData.item_count
        });
        
        if (currentItemCount !== this.lastItemCount) {
          console.log('Cart item count changed:', {
            from: this.lastItemCount,
            to: currentItemCount,
            action: currentItemCount > this.lastItemCount ? 'ADDED' : 'REMOVED'
          });
          
          this.cartProductIds = currentCartIds;
          this.lastCartIds = currentCartIds.join(',');
          this.lastItemCount = currentItemCount;
          
          const container = document.querySelector('[data-cart-product-ids]');
          if (container) {
            container.setAttribute('data-cart-product-ids', this.lastCartIds);
          }
          
          await this.loadRecommendations();
        } else {
          console.log('Cart item count unchanged - no recommendations update needed');
          this.cartProductIds = currentCartIds;
          this.lastCartIds = currentCartIds.join(',');
        }
        
      } catch (error) {
        console.error('Error checking cart state:', error);
      }
    }

    async onCartDrawerOpen() {
      if (window.innerWidth >= 750) {
        this.style.width = '50%';
        this.isFloating = true;
      }
      
      console.log('Cart drawer opened - checking cart state and loading recommendations');
      await this.checkCartStateAndUpdate();
    }

    onCartDrawerClose() {
      if (window.innerWidth >= 750) {
        this.style.display = 'block';
        this.isFloating = true;
      }
    }

    async loadRecommendations() {
      if (this.isLoading) return;

      this.isLoading = true;
      console.log('Loading Search & Discovery recommendations for cart products:', this.cartProductIds);
      
      if (!this.cartProductIds || this.cartProductIds.length === 0 || (this.cartProductIds.length === 1 && this.cartProductIds[0] === '')) {
        console.log('Cart is empty, hiding recommendations');
        this.style.display = 'none';
        this.isLoading = false;
        return;
      }
      
      console.log('Cart has products, showing recommendations container');
      this.style.display = 'block';
      this.style.visibility = 'visible';
      
      try {
        const recommendations = await this.fetchSearchDiscoveryRecommendations();
        
        if (recommendations && recommendations.length > 0) {
          const grid = document.createElement('div');
          grid.className = 'cart-drawer-recommendations__grid';
          
          const itemPromises = recommendations.map(product => this.createRecommendationItem(product));
          const items = await Promise.all(itemPromises);
          
          items.forEach(item => {
            grid.appendChild(item);
          });
          
          this.innerHTML = grid.outerHTML;
          this.setupAddToCartButtons();
          
          // Setup variant selectors with debugging
          setTimeout(() => {
            console.log('About to setup variant selectors after DOM update...');
            console.log('this object:', this);
            console.log('this.setupVariantSelectors:', this.setupVariantSelectors);
            if (typeof this.setupVariantSelectors === 'function') {
              console.log('Calling setupVariantSelectors...');
              this.setupVariantSelectors();
            } else {
              console.error('setupVariantSelectors is not a function!');
            }
          }, 100);
          
          this.addCloseButton();
          this.isLoaded = true;
          console.log('Loaded', recommendations.length, 'Search & Discovery recommendations');
        } else {
          console.log('No recommendations found');
          this.showFallback();
        }
        
      } catch (error) {
        console.error('Error loading Search & Discovery recommendations:', error);
        this.showFallback();
      } finally {
        this.isLoading = false;
      }
    }

    async fetchSearchDiscoveryRecommendations() {
      try {
        if (this.cartProductIds.length === 0) return null;
        
        let allRecommendations = [];
        
        for (const productId of this.cartProductIds) {
          const recommendationsUrl = `/recommendations/products.json?product_id=${productId}&limit=4&intent=related`;
          
          console.log(`Fetching recommendations for product ${productId}:`, recommendationsUrl);
          
          const response = await fetch(recommendationsUrl);
          if (!response.ok) {
            console.warn(`Failed to get recommendations for product ${productId}: ${response.status}`);
            continue;
          }
          
          const data = await response.json();
          const products = data.products || [];
          
          console.log(`Product ${productId} returned ${products.length} recommendations`);
          
          products.forEach(product => {
            const productIdStr = product.id.toString();
            if (!this.cartProductIds.includes(productIdStr) && 
                !allRecommendations.some(rec => rec.id.toString() === productIdStr)) {
              allRecommendations.push(product);
            }
          });
        }
        
        console.log('Total unique recommendations from all cart products:', allRecommendations.length);
        return allRecommendations.slice(0, this.productLimit);
      } catch (error) {
        console.error('Search & Discovery API error:', error);
        return null;
      }
    }

    async createRecommendationItem(product) {
      const item = document.createElement('div');
      item.className = 'cart-drawer-recommendations__item';
      item.setAttribute('data-product-id', product.id);
      
      // Handle Search & Discovery product format
      const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
      const price = variant ? (variant.price / 100).toFixed(2) : '0.00';
      const comparePrice = variant && variant.compare_at_price ? (variant.compare_at_price / 100).toFixed(2) : null;
      
      // Handle image
      let imageUrl = '/assets/no-image.svg';
      if (product.featured_image) {
        imageUrl = product.featured_image;
      } else if (product.images && product.images.length > 0) {
        imageUrl = product.images[0];
      } else if (product.featured_media && product.featured_media.preview_image) {
        imageUrl = product.featured_media.preview_image.src;
      }
      
      // Format price with currency
      const formattedPrice = new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK'
      }).format(price);
      
      const formattedComparePrice = comparePrice ? new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK'
      }).format(comparePrice) : null;
      
      // Generate variation selectors if product has variants
      let variationSelectors = '';
      if (product.variants && product.variants.length > 1) {
        const options = product.options || [];
        const selectedVariant = variant;
        
        options.forEach((option, optionIndex) => {
          const optionName = option.name;
          const optionValues = option.values;
          
          variationSelectors += `
            <div class="cart-drawer-recommendation-item__variant-selector">
                <label class="cart-drawer-recommendation-item__variant-label">${optionName}:</label>
                <div class="drawer_vs_wrap">
                    <select class="cart-drawer-recommendation-item__variant-select" data-option-index="${optionIndex}">
                        ${optionValues.map(value => {
                        const selected = selectedVariant && selectedVariant.options && selectedVariant.options[optionIndex] === value ? 'selected' : '';
                        return `<option value="${value}" ${selected}>${value}</option>`;
                        }).join('')}
                    </select>
                </div>
            </div>
          `;
        });
      }
      
      item.innerHTML = `
        <div class="cart-drawer-recommendation-item">
          <a href="${product.url || `/products/${product.handle}`}" class="global_drawer_link"></a>
          <div class="drawer_item_rec cart-drawer-recommendation-item__link">
            <div class="cart-drawer-recommendation-item__image">
              <div class="cart-drawer-recommendation-item__bg">
                <img src="${imageUrl}" alt="${product.title}" loading="lazy">
              </div>
            </div>
            <div class="cart-drawer-recommendation-item__content">
              <h4 class="cart-drawer-recommendation-item__title">${product.title}</h4>
              <div class="cart-drawer-recommendation-item__price" data-price-container>
                ${comparePrice ? `
                  <span class="cart-drawer-recommendation-item__price--sale" data-price>${formattedPrice}</span>
                  <span class="cart-drawer-recommendation-item__price--compare" data-compare-price>${formattedComparePrice}</span>
                ` : `
                  <span class="cart-drawer-recommendation-item__price--regular" data-price>${formattedPrice}</span>
                `}
              </div>
              ${variationSelectors}
              <div class="cart-drawer-recommendation-item__actions">
                <button type="button" class="btn btn--primary btn--solid btn--small cart-drawer-recommendation-item__add-btn" data-add-to-cart data-product-id="${product.id}" data-variant-id="${variant ? variant.id : ''}">
                  <span class="btn__text">${this.addToCartText}</span>
                </button>
              </div>
            </div>
          </div>
        </div>`;
      
      // Store product data for variant selection
      item.productData = product;
      
      // Also store in the class instance for backup access
      this.storedProductData[product.id] = product;
      
      console.log('Created recommendation item:', {
        productId: product.id,
        hasVariants: product.variants && product.variants.length > 1,
        variantCount: product.variants ? product.variants.length : 0,
        productData: item.productData,
        storedInClass: this.storedProductData[product.id] ? 'yes' : 'no'
      });
      
      // Set up variant selectors immediately if this product has variants
      if (product.variants && product.variants.length > 1) {
        console.log('Setting up variants immediately for product:', product.id);
        this.setupVariantSelectorsForItem(item);
      }
      
      return item;
    }

    showFallback() {
      const grid = document.createElement('div');
      grid.className = 'cart-drawer-recommendations__grid';
      
      grid.innerHTML = `
        <div class="cart-drawer-recommendations__fallback">
          <p>Recommendations will be available soon!</p>
          <p><small>Add more products to your cart to see related items.</small></p>
        </div>
      `;
      
      this.innerHTML = grid.outerHTML;
      this.addCloseButton();
      this.isLoaded = true;
      console.log('Loaded fallback recommendations');
    }

    addCloseButton() {
      if (window.innerWidth >= 750) {
        const closeButton = document.createElement('button');
        closeButton.className = 'cart-drawer-recommendations__close';
        closeButton.innerHTML = '×';
        closeButton.setAttribute('aria-label', 'Close recommendations');
        
        closeButton.addEventListener('click', () => {
          this.style.display = 'none';
          document.dispatchEvent(new CustomEvent('cart:close', { bubbles: true }));
        });
        
        this.insertBefore(closeButton, this.firstChild);
      }
    }

    setupAddToCartButtons() {
      const addToCartButtons = this.querySelectorAll('[data-add-to-cart]');
      addToCartButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const productId = button.getAttribute('data-product-id');
          this.addToCart(productId, button);
        });
      });
    }

    setupVariantSelectorsForItem(item) {
      console.log('Setting up variant selectors for individual item:', item);
      
      const productData = item.productData;
      if (!productData || !productData.variants || productData.variants.length <= 1) {
        console.log('Item has no variants, skipping setup');
        return;
      }
      
      console.log('Item has variants, setting up...');
      
      // Initialize the default variant selection
      this.initializeVariantSelection(item);
      
      // Set up event listeners for variant selectors
      const variantSelects = item.querySelectorAll('.cart-drawer-recommendation-item__variant-select');
      console.log('Found variant selects for item:', variantSelects.length);
      
      variantSelects.forEach((select, selectIndex) => {
        console.log(`Setting up variant select ${selectIndex}:`, select);
        select.addEventListener('change', (e) => {
          console.log(`Variant select ${selectIndex} changed:`, e.target.value);
          this.updateVariantSelection(item);
        });
      });
    }

    setupVariantSelectors() {
      console.log('Setting up variant selectors...');
      
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        const items = this.querySelectorAll('.cart-drawer-recommendations__item');
        console.log('Found items for variant setup:', items.length);
        
        items.forEach((item, index) => {
          console.log(`Setting up variants for item ${index}:`, item);
          console.log(`Item ${index} productData:`, item.productData);
          
          if (item.productData && item.productData.variants && item.productData.variants.length > 1) {
            console.log(`Item ${index} has variants:`, item.productData.variants.length);
            this.setupVariantSelectorsForItem(item);
          } else {
            console.log(`Item ${index} has no variants or no productData`);
          }
        });
        
        // Also set up event delegation as a backup
        this.addEventListener('change', (e) => {
          if (e.target.classList.contains('cart-drawer-recommendation-item__variant-select')) {
            console.log('Variant selector changed via event delegation:', e.target.value);
            const item = e.target.closest('.cart-drawer-recommendations__item');
            if (item) {
              console.log('Found item via event delegation:', item);
              this.updateVariantSelection(item);
            }
          }
        });
        
      }, 100);
    }

    initializeVariantSelection(item) {
      console.log('initializeVariantSelection called with item:', item);
      console.log('item.productData:', item.productData);
      
      let productData = item.productData;
      
      if (!productData) {
        console.error('productData is undefined for item in initialization:', item);
        
        // Try to get product data from stored data
        const productId = item.getAttribute('data-product-id');
        if (productId) {
          productData = this.getStoredProductData(productId);
          if (productData) {
            console.log('Found stored product data for initialization:', productData);
            item.productData = productData;
          }
        }
        
        if (!productData) {
          console.error('Cannot initialize variants without product data');
          return;
        }
      }
      
      if (!productData.variants) {
        console.error('No variants in product data for initialization:', productData);
        return;
      }

      const selects = item.querySelectorAll('.cart-drawer-recommendation-item__variant-select');
      const selectedOptions = Array.from(selects).map(select => select.value);

      console.log('Initializing variant selection:', {
        productId: productData.id,
        selectedOptions,
        variants: productData.variants.map(v => ({ id: v.id, options: v.options }))
      });

      // Find matching variant
      const selectedVariant = productData.variants.find(variant => {
        if (!variant.options) return false;
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });

      console.log('Initial selected variant:', selectedVariant);

      if (selectedVariant) {
        // Update add to cart button variant ID
        const addToCartButton = item.querySelector('[data-add-to-cart]');
        if (addToCartButton) {
          addToCartButton.setAttribute('data-variant-id', selectedVariant.id);
          console.log('Initialized variant ID on button:', selectedVariant.id);
        }
      } else {
        console.error('No matching variant found for initialization with options:', selectedOptions);
      }
    }

    updateVariantSelection(item) {
      console.log('updateVariantSelection called with item:', item);
      console.log('item.productData:', item.productData);
      
      const productData = item.productData;
      if (!productData) {
        console.error('productData is undefined for item:', item);
        
        // Try to get product data from the item's data attribute
        const productId = item.getAttribute('data-product-id');
        console.log('Trying to get product data from productId:', productId);
        
        // Try to find the product data from the recommendations
        if (productId) {
          // Store product data in a way that's accessible
          const storedData = this.getStoredProductData(productId);
          if (storedData) {
            console.log('Found stored product data:', storedData);
            item.productData = storedData;
            this.updateVariantSelection(item); // Recursive call with data
            return;
          }
        }
        
        console.error('Cannot proceed without product data');
        return;
      }
      
      if (!productData.variants) {
        console.error('No variants in product data:', productData);
        return;
      }

      const selects = item.querySelectorAll('.cart-drawer-recommendation-item__variant-select');
      const selectedOptions = Array.from(selects).map(select => select.value);

      console.log('Variant selection update:', {
        productId: productData.id,
        selectedOptions,
        variants: productData.variants.map(v => ({ id: v.id, options: v.options }))
      });

      // Find matching variant
      const selectedVariant = productData.variants.find(variant => {
        if (!variant.options) return false;
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });

      console.log('Selected variant:', selectedVariant);

      if (selectedVariant) {
        // Update price
        const price = (selectedVariant.price / 100).toFixed(2);
        const comparePrice = selectedVariant.compare_at_price ? (selectedVariant.compare_at_price / 100).toFixed(2) : null;
        
        const formattedPrice = new Intl.NumberFormat('da-DK', {
          style: 'currency',
          currency: 'DKK'
        }).format(price);
        
        const formattedComparePrice = comparePrice ? new Intl.NumberFormat('da-DK', {
          style: 'currency',
          currency: 'DKK'
        }).format(comparePrice) : null;

        const priceContainer = item.querySelector('[data-price-container]');
        if (priceContainer) {
          if (comparePrice) {
            priceContainer.innerHTML = `
              <span class="cart-drawer-recommendation-item__price--sale" data-price>${formattedPrice}</span>
              <span class="cart-drawer-recommendation-item__price--compare" data-compare-price>${formattedComparePrice}</span>
            `;
          } else {
            priceContainer.innerHTML = `
              <span class="cart-drawer-recommendation-item__price--regular" data-price>${formattedPrice}</span>
            `;
          }
        }

        // Update add to cart button variant ID
        const addToCartButton = item.querySelector('[data-add-to-cart]');
        if (addToCartButton) {
          addToCartButton.setAttribute('data-variant-id', selectedVariant.id);
          console.log('Updated variant ID on button:', selectedVariant.id);
        }
      } else {
        console.error('No matching variant found for selected options:', selectedOptions);
      }
    }

    async addToCart(productId, button) {
      const buttonText = button.querySelector('.btn__text');
      const originalText = buttonText.textContent;
      const variantId = button.getAttribute('data-variant-id');
      
      console.log('Adding to cart:', {
        productId,
        variantId,
        buttonElement: button,
        allAttributes: Array.from(button.attributes).map(attr => `${attr.name}="${attr.value}"`)
      });
      
      try {
        buttonText.textContent = 'Adding...';
        button.disabled = true;
        button.classList.add('is-loading');
        
        // Use variant ID if available, otherwise use product ID
        const addToCartId = variantId || productId;
        
        console.log('Using addToCartId:', addToCartId);
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: addToCartId,
            quantity: 1
          })
        });
        
        if (response.ok) {
          buttonText.textContent = 'Added!';
          button.classList.remove('is-loading');
          
          // Trigger theme cart events
          document.dispatchEvent(new CustomEvent('theme:cart:add', { 
            bubbles: true, 
            detail: { productId, variantId: addToCartId } 
          }));
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          
          // Reset button after delay
          setTimeout(() => {
            buttonText.textContent = originalText;
            button.disabled = false;
          }, 2000);
          
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        buttonText.textContent = 'Error';
        button.classList.remove('is-loading');
        setTimeout(() => {
          buttonText.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    }

    // Get stored product data by product ID
    getStoredProductData(productId) {
      console.log('Getting stored product data for productId:', productId);
      console.log('Available stored data:', Object.keys(this.storedProductData));
      return this.storedProductData[productId] || null;
    }

    // Public method to manually trigger cart refresh
    forceRefresh() {
      console.log('Force refresh triggered');
      this.refreshCartDataAndReload();
    }

    async refreshCartDataAndReload() {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          const cartData = await response.json();
          const newProductIds = cartData.items.map(item => item.product_id.toString());
          const newCartIds = newProductIds.join(',');
          
          console.log('Fresh cart data:', {
            newProductIds,
            oldProductIds: this.cartProductIds,
            newCartIds,
            oldCartIds: this.lastCartIds,
            cartItemsCount: cartData.items.length
          });
          
          const wasEmpty = !this.cartProductIds || this.cartProductIds.length === 0 || (this.cartProductIds.length === 1 && this.cartProductIds[0] === '');
          const nowHasItems = newProductIds.length > 0;
          
          if (wasEmpty && nowHasItems) {
            console.log('First product detected in cart - showing recommendations');
          }
          
          this.cartProductIds = newProductIds;
          this.lastCartIds = newCartIds;
          
          const container = document.querySelector('[data-cart-product-ids]');
          if (container) {
            container.setAttribute('data-cart-product-ids', newCartIds);
          }
          
          this.loadRecommendations();
        } else {
          console.error('Failed to fetch cart data');
        }
      } catch (error) {
        console.error('Error refreshing cart data:', error);
      }
    }
  }

  if (!customElements.get('cart-drawer-recommendations')) {
    customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
  }

  // Global function to manually refresh cart recommendations
  window.refreshCartRecommendations = function() {
    const recommendations = document.querySelector('cart-drawer-recommendations');
    if (recommendations && typeof recommendations.forceRefresh === 'function') {
      recommendations.forceRefresh();
    }
  };

}();